import IUIActions from "./IUIActions";
import { inject, injectable } from "inversify";
import SERVICE_IDENTIFIER from "../constants/identifiers";
import IAudio from "../engine/IAudio";
import Wallet from "../wallet/Wallet";
import CommanderBase from "../commander/CommanderBase";
import events from "../events/EventsObservable";
import userActionInstance, { ACTION_EVENTS } from "./UserAction";
import IGame from "../game/IGame";
import IStatesManager from "../states/IStatesManager";
import { UIMESSAGES_EVENTS } from "./messages/UIMessages";
import { getIOSVersion, isAndroid, isIOS } from "../device/device";
import KeydownHandler from "./KeydownHandler";
import { GAME_EVENTS } from "../game/gameEvents";

@injectable()
export default class AbstractUIActions implements IUIActions {
	protected statesManager: IStatesManager;
	protected audio: IAudio;
	protected wallet: Wallet;
	protected commander: CommanderBase;
	protected game: IGame;
	protected keydownHandler: KeydownHandler;

	protected allowedActionsDynamic: Record<string, boolean>;
	protected allowedActionsStatic: Record<string, boolean>;
	protected blockedActions: Record<string, boolean> = {};

	protected locked: boolean = false;
	protected paused: boolean = false;

	constructor(
		@inject(SERVICE_IDENTIFIER.StatesManager) states: IStatesManager,
		@inject(SERVICE_IDENTIFIER.EngineAudio) audio: IAudio,
		@inject(SERVICE_IDENTIFIER.Wallet) wallet: Wallet,
		@inject(SERVICE_IDENTIFIER.Commander) commander: CommanderBase
	) {
		this.statesManager = states;
		this.audio = audio;
		this.wallet = wallet;
		this.commander = commander;
		this.game = commander.game;

		this.allowedActionsDynamic = {
			["*"]: false, //all actions
		};
		this.allowedActionsStatic = {
			[ACTION_EVENTS.ACTION_FORCESTOP]: true,
			[ACTION_EVENTS.ACTION_AUTOSTART_STOP]: true,
			[ACTION_EVENTS.ACTION_FULLSCREEN_EXIT_REQUEST]: true,
			[ACTION_EVENTS.ACTION_FULLSCREEN_REQUEST]: true,
			[ACTION_EVENTS.ACTION_SOUND_TOGGLE]: true,
		};

		events.subscribe(UIMESSAGES_EVENTS.UIMESSAGE_OPEN, () => {
			this.lock();
		});

		events.subscribe(UIMESSAGES_EVENTS.UIMESSAGE_CLOSE, () => {
			this.unlock();
		});

		events.subscribe(GAME_EVENTS.GAME_PAUSED, (_: string, paused: boolean) => {
			this.paused = paused;
		});
	}

	init() {
		this.initTouch();
		this.initKeys();
	}

	lock() {
		this.unsetAllowedActions();
		this.locked = true;
	}

	unlock() {
		this.locked = false;
		this.statesManager.reset();
	}

	public allowAction(action: string) {
		if (this.locked) return;
		this.allowedActionsDynamic[action] = true;
	}

	public allowActions(actions: string[]) {
		if (this.locked) return;
		for (let action of actions) {
			this.allowedActionsDynamic[action] = true;
		}
	}

	public allowAllActions(allow: boolean = true) {
		if (this.locked) return;
		this.allowedActionsDynamic["*"] = allow;
	}

	public setAllowedAction(actions: Record<string, boolean>) {
		if (this.locked) return;
		this.allowedActionsDynamic = actions;
	}

	public unsetAllowedActions() {
		if (this.locked) return;
		this.allowedActionsDynamic = {};
	}

	public blockActions(actions: string[], block: boolean) {
		for (let action of actions) {
			this.blockedActions[action] = block;
		}
	}

	public isActionAllowed(action) {
		if (this.paused) return false;
		return (
			!this.blockedActions[action] &&
			(this.allowedActionsStatic[action] ||
				this.allowedActionsDynamic["*"] ||
				this.allowedActionsDynamic[action])
		);
	}

	initTouch() {
		$(".mx-input")
			.unbind("click")
			.bind("click", (event) => {
				if (!event.originalEvent?.isTrusted) {
					console.log("mx-input clicked by script, ignoring.", event);
					return;
				}
				this.click(event);
			});
	}

	initKeys() {
		this.keydownHandler = new KeydownHandler(
			this.wallet,
			this,
			this.statesManager,
			this.audio
		);
		this.keydownHandler.init()
	}

	click(event) {
		event.stopPropagation();
		event.preventDefault();

		let element = event.currentTarget;
		let action = $(element).data("action");
		let data = $(element).data("data");

		if (!this.isActionAllowed(action)) return;

		userActionInstance.trigger(action, data);
	}
}
